import React, { useCallback, useEffect, useState } from 'react';
import { trainingApi } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { BookOpen, Loader2, Lock, LockOpen, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import UploadFileDialog from './UploadFileDialog';
import UnlockManager from './UnlockManager';

export default function ManualTab({ meta }) {
  const { user } = useAuth();
  const [files, setFiles] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [openingId, setOpeningId] = useState(null);

  const load = useCallback(() => {
    trainingApi.files('manual')
      .then(r => setFiles(r.data))
      .catch(e => { toast.error(e.response?.data?.detail || 'Errore caricamento manuale'); setFiles([]); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const openFile = async (f) => {
    setOpeningId(f.file_id);
    try {
      const r = await trainingApi.downloadFile(f.file_id);
      window.open(URL.createObjectURL(new Blob([r.data], { type: f.content_type || 'application/pdf' })), '_blank');
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'Capitolo non ancora sbloccato' : 'Errore apertura file');
    } finally {
      setOpeningId(null);
    }
  };

  const doDelete = async (f) => {
    if (!window.confirm(`Eliminare il capitolo "${f.title}"?`)) return;
    try {
      await trainingApi.deleteFile(f.file_id);
      toast.success('Capitolo eliminato');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eliminazione fallita'); }
  };

  const fmtSize = (n) => n ? (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`) : '';

  return (
    <div data-testid="manual-tab">
      {meta.can_manage && files && (
        <UnlockManager files={files}
          title="Sblocco capitoli per dipendente"
          description="I dipendenti vedono solo i capitoli sbloccati per loro. Seleziona un dipendente e attiva i capitoli man mano che il percorso avanza." />
      )}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">{files ? `${files.length} capitoli` : ''}</p>
        {meta.can_manage && (
          <Button onClick={() => setShowUpload(true)} className="gap-1.5" data-testid="upload-manual-btn">
            <Upload className="w-4 h-4" /> Carica capitolo
          </Button>
        )}
      </div>

      {files === null ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : files.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center text-slate-400" data-testid="manual-empty">
          <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>Nessun capitolo del manuale</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {files.map(f => (
            <div key={f.file_id}
                 className={`border p-4 flex flex-col gap-2 ${f.locked
                   ? 'bg-slate-50 border-slate-200 opacity-70'
                   : 'bg-white border-slate-200'}`}
                 data-testid={`manual-card-${f.file_id}`}>
              <div className="flex items-start justify-between gap-2">
                <span className="font-heading text-2xl font-bold text-slate-200 tabular-nums leading-none">
                  {f.chapter_no != null ? String(f.chapter_no).padStart(2, '0') : '—'}
                </span>
                {f.locked
                  ? <Badge variant="outline" className="bg-slate-100 text-slate-500 gap-1"><Lock className="w-3 h-3" /> Bloccato</Badge>
                  : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 gap-1"><LockOpen className="w-3 h-3" /> Disponibile</Badge>}
              </div>
              <p className="text-sm font-medium text-slate-900 flex-1">{f.title}</p>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-400">{fmtSize(f.size)}</span>
                <div className="flex gap-1.5">
                  {user?.role === 'operations_manager' && !f.locked && (
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600"
                            onClick={() => doDelete(f)} data-testid={`manual-delete-${f.file_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  {!f.locked && (
                    <Button variant="outline" size="sm" className="h-7 rounded-none gap-1.5 text-xs"
                            onClick={() => openFile(f)} disabled={openingId === f.file_id}
                            data-testid={`manual-open-${f.file_id}`}>
                      {openingId === f.file_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <BookOpen className="w-3 h-3" />} Apri
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadFileDialog kind="manual" onClose={() => setShowUpload(false)}
                          onUploaded={() => { setShowUpload(false); load(); }} />
      )}
    </div>
  );
}
